import { useTranslation } from 'react-i18next'

import StarRating from '@/components/ui/star-rating'
import { RatingSummary } from '@/modules/InternalRating/types'
import { cn } from '@/lib/utils'

interface Props {
    summary?: RatingSummary
    /** Mức sao đang lọc, để tô đậm thanh tương ứng. */
    activeStar?: number
    /** Bấm thanh phân bố = lọc theo mức sao đó (bấm lại = bỏ lọc). */
    onStarSelect?: (star: number) => void
}

// Dải tổng quan tab "Điểm của tôi": điểm TB + tổng lượt + phân bố sao.
//
// Trả null khi CHƯA có dữ liệu hoặc backend chưa trả summary (FE lên staging trước BE).
// Cố ý KHÔNG `?? 0`: hiện "0.0 sao" khi chưa có dữ liệu là bịa số liệu.
//
// Số liệu ở đây luôn là TOÀN BỘ đánh giá, không theo filter — nó là "điểm của tôi",
// không phải "điểm của kết quả đang lọc".
const RatingSummaryCard = ({ summary, activeStar, onStarSelect }: Props) => {
    const { t } = useTranslation('rating')

    if (!summary || summary.total_ratings <= 0) return null

    const buckets = [...(summary.distribution ?? [])].sort(
        (a, b) => b.star - a.star
    )
    const max = Math.max(...buckets.map((b) => b.count), 1)

    return (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <span className="text-3xl font-semibold leading-none text-gray-800">
                    {summary.average_rating.toFixed(1)}
                </span>
                <StarRating
                    value={summary.average_rating}
                    readOnly
                    precise
                    size="sm"
                    ariaLabel={`${summary.average_rating.toFixed(1)}/5`}
                />
                <span className="text-sm text-gray-500">
                    {t('page.total_ratings', { count: summary.total_ratings })}
                </span>
            </div>

            <div className="mt-3 space-y-1">
                {buckets.map((b) => (
                    <button
                        key={b.star}
                        type="button"
                        disabled={!onStarSelect}
                        aria-pressed={activeStar === b.star}
                        // aria-label ghi đè toàn bộ nội dung con, nên PHẢI chứa cả
                        // b.count — nếu không, người dùng screen reader không bao giờ
                        // nghe được số lượt của từng mức sao (nó chỉ tồn tại ở đây).
                        aria-label={`${t('page.filter_by_star', {
                            star: b.star,
                        })} (${b.count})`}
                        className={cn(
                            'flex w-full items-center gap-2 rounded px-1 py-0.5 text-left',
                            onStarSelect &&
                                'transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary',
                            activeStar === b.star && 'bg-yellow-50'
                        )}
                        onClick={() => onStarSelect?.(b.star)}
                    >
                        <span className="w-8 shrink-0 text-xs text-gray-500">
                            {b.star}★
                        </span>
                        <span className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100">
                            <span
                                className="block h-full rounded-full bg-yellow-400"
                                style={{ width: `${(b.count / max) * 100}%` }}
                            />
                        </span>
                        <span className="w-8 shrink-0 text-right text-xs text-gray-500">
                            {b.count}
                        </span>
                    </button>
                ))}
            </div>
        </div>
    )
}

export default RatingSummaryCard
